import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Search, Lightbulb, Wrench, Cog, TrendingUp, ArrowRight } from 'lucide-react';

const steps = [
  {
    number: '01',
    icon: Search,
    title: 'Diagnostic',
    description: 'On analyse votre situation, vos outils, votre parcours client et vos blocages actuels.',
  },
  {
    number: '02',
    icon: Lightbulb,
    title: 'Stratégie',
    description: 'On définit les priorités et la feuille de route adaptée à vos objectifs.',
  },
  {
    number: '03',
    icon: Wrench,
    title: 'Mise en place',
    description: 'On implémente les solutions validées avec vous, étape par étape.',
  },
  {
    number: '04',
    icon: Cog,
    title: 'Automatisation',
    description: 'On connecte et automatise pour que le système tourne seul.',
  },
  {
    number: '05',
    icon: TrendingUp,
    title: 'Optimisation',
    description: 'On suit les résultats et on ajuste en continu pour maximiser l\'impact.',
  },
];

export const MethodPreview = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-hero" />
      <div className="hero-glow" />

      <div className="section-container relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="lg:sticky lg:top-32"
          >
            <span className="section-label mb-4 inline-block">
              Notre méthode
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
              Une approche{' '}
              <span className="gradient-text">structurée</span>
              {' '}et progressive
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Tout commence par un diagnostic. On ne pousse jamais une solution sans 
              comprendre le besoin réel. Vous ne savez pas exactement ce qu'il vous faut ? 
              C'est normal. On commence par comprendre, puis on construit.
            </p>

            <div className="p-6 rounded-xl bg-card/50 border border-border/50 mb-8">
              <p className="text-sm text-muted-foreground italic">
                "On ne vend pas d'outils, on met en place des systèmes qui génèrent 
                des résultats mesurables."
              </p>
            </div>

            <Link to="/methode">
              <Button variant="hero" size="lg" className="btn-glow group">
                Découvrir notre méthode complète
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>

          {/* Right - Steps */}
          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-7 top-8 bottom-8 w-0.5 bg-gradient-to-b from-primary via-primary/50 to-transparent hidden sm:block" />

            <div className="space-y-6">
              {steps.map((step, index) => (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, x: 30 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                  className="relative flex gap-6"
                >
                  {/* Number Badge */}
                  <div className="number-badge shrink-0 relative z-10">
                    {step.number}
                  </div>

                  {/* Content Card */}
                  <div className="flex-1 p-6 rounded-xl card-gradient border border-border/50 hover:border-primary/30 transition-colors">
                    <div className="flex items-start gap-4">
                      <div className="feature-icon shrink-0">
                        <step.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                          {step.title}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
